import React, { ErrorInfo, JSX, lazy, Suspense } from 'react';
import { getModuleFactory } from '../factories/getModuleFactory';
import { useDynamicScript } from '../hooks/useDynamicScript';
import {
  modulePlaceholderMap,
  MODULE_STATUS_FAILED_LOADING_SCRIPT,
  MODULE_STATUS_LOADING_SCRIPT,
  MODULE_STATUS_NOT_SPECIFIED,
  MODULE_STATUS_ERROR_RENDERING_MODULE
} from './ModulePlaceholders';
import { ErrorBoundary } from 'react-error-boundary';

export type ModuleConfig = {
  url: string;
  appName: string;
  module: string;
};

export type ModuleProps = {
  module: ModuleConfig;
  moduleProps?: any;
  log?: (msg: string) => void;
  error?: (msg: string) => void;
  trace?: string;
  loadingElement?: JSX.Element;
};

const logError = (errorOnComponentTree: Error, info: ErrorInfo) => {
  console.error('error on component tree -', errorOnComponentTree, info.componentStack);
};

const errorFallback = modulePlaceholderMap[MODULE_STATUS_ERROR_RENDERING_MODULE]();

export const Module = ({ module, moduleProps = {}, log = () => {}, error = () => {}, loadingElement }: ModuleProps) => {
  const { ready, failed } = useDynamicScript({ url: module?.url, log, error });

  if (!module?.url || !module?.appName || !module?.module) {
    return modulePlaceholderMap[MODULE_STATUS_NOT_SPECIFIED](module?.url, module);
  }

  if (!ready && !failed) {
    return loadingElement ?? modulePlaceholderMap[MODULE_STATUS_LOADING_SCRIPT](module.url);
  }

  if (failed) {
    return modulePlaceholderMap[MODULE_STATUS_FAILED_LOADING_SCRIPT](module.url);
  }

  const { factory, isLazy } = getModuleFactory(module.appName, module.module, log);
  if (isLazy) {
    const Component = lazy(factory);

    return (
      <Suspense fallback={loadingElement ?? 'Loading Module'}>
        <ErrorBoundary fallback={errorFallback} onError={logError}>
          <Component {...moduleProps} />
        </ErrorBoundary>
      </Suspense>
    );
  }

  const Component = factory.default;

  return (
    <ErrorBoundary fallback={errorFallback} onError={logError}>
      <Component {...moduleProps} />
    </ErrorBoundary>
  );
};
